<template>
  <div class="responses-view">
    <!-- Header with title and accepting responses toggle -->
    <div class="d-flex justify-space-between align-center mb-6">
      <h1 class="text-h4 font-weight-bold">Responses</h1>
    </div>

    <!-- Tabs for response status -->
    <v-tabs
      v-model="activeTab"
      background-color="transparent"
      class="response-tabs mb-4"
      slider-color="#e94057"
      active-class="active-tab"
    >
      <v-tab value="all">All</v-tab>
      <v-tab value="completed">Completed</v-tab>
      <v-tab value="ongoing">Ongoing</v-tab>
    </v-tabs>

    <!-- Responses table -->
    <v-card elevation="1" class="response-table-card">
      <DataTable
        :headers="tableHeaders"
        :items="filteredAssessments"
        :loading="loading"
        :current-page="1"
        :total-pages="1"
        :items-per-page="-1"
        :show-pagination="false"
        class="response-table"
        @page-change="onPageChange"
      >
        <!-- Name column -->
        <template #column-title="{ item }">
          <div class="font-weight-medium">{{ item.title }}</div>
        </template>
        <!-- Date column -->
        <template #column-created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        <!-- Deadline column -->
        <template #column-deadline="{ item }">
          {{ formatDate(item.deadline) }}
        </template>
        <!-- Brand column -->
        <template #column-visual_identity_system_name="{ item }">
          {{ item.visual_identity_system_name }}
        </template>
        <!-- Completion status column -->
        <template #column-completion="{ item }">
          <div class="d-flex align-center">
            <div class="custom-progress-container mr-2">
              <div class="custom-progress-track">
                <div
                  class="custom-progress-fill"
                  :style="{ width: Math.round(getCompletionPercentage(item)) + '%' }"
                ></div>
              </div>
            </div>
            <span class="text-caption font-weight-medium">
              {{ Math.round(getCompletionPercentage(item)) }}% 
              ({{ getCompletedCount(item) }}/{{ getTotalExperts(item) }})
            </span>
          </div>
        </template>
        <!-- Overall status column -->
        <template #column-status="{ item }">
          <div class="d-flex justify-center">
            <v-chip
              small
              :color="getStatusColor(item)"
              text-color="white"
              label
            >
              {{ getStatusText(item) }}
            </v-chip>
          </div>
        </template>
        <!-- Action column -->
        <template #column-action="{ item }">
          <ActionButton 
            variant="primary" 
            rightIcon="mdi-chevron-right" 
            @click="goToDetail(item)"
          >
            View responses
          </ActionButton>
        </template>
      </DataTable>
    </v-card>
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import DataTable from '@/components/DataTable.vue';
import ActionButton from '@/components/common/ActionButton.vue';

export default {
  name: 'ResponsesView',
  components: { DataTable, ActionButton },
  data() {
    return {
      loading: false,
      assessments: [],
      page: 1,
      itemsPerPage: -1,
      totalItems: 0,
      activeTab: 'all',
      tableHeaders: [
        { text: 'Name', value: 'title', align: 'start', sortable: true },
        { text: 'Brand', value: 'visual_identity_system_name', align: 'start', sortable: true },
        { text: 'Date', value: 'created_at', sortable: true },
        { text: 'Deadline', value: 'deadline', sortable: true },
        { text: 'Status', value: 'completion', sortable: false },
        { text: 'Status', value: 'status', align: 'center', sortable: false },
        { text: '', value: 'action', align: 'center', sortable: false },
      ],
    };
  },
  computed: {
    totalPages() { return 1; },
    filteredAssessments() {
      let filtered = [...this.assessments];
      if (this.activeTab === 'completed') {
        filtered = filtered.filter(item => Math.round(this.getCompletionPercentage(item)) === 100);
      } else if (this.activeTab === 'ongoing') {
        filtered = filtered.filter(
          item => Math.round(this.getCompletionPercentage(item)) < 100
        );
      }
      return filtered;
    }
  },
  methods: {
    fetchAssessments() {
      this.loading = true;
      apiService.getAssessments({
        is_active: this.acceptingResponses
      })
      .then(response => {
        const data = response.data;
        this.assessments = Array.isArray(data) ? data : (data?.results || []);
        this.totalItems = this.assessments.length;
      })
      .catch(error => {
        console.error('Error fetching assessments:', error);
      })
      .finally(() => {
        this.loading = false;
      });
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.toLocaleString('default', { month: 'short' })} ${date.getDate()} ${date.getFullYear()}`;
    },
    padCode(id) {
      return id ? id.toString().padStart(8, '0') : '00000000';
    },
    getStatusText(item) {
      return Math.round(this.getCompletionPercentage(item)) === 100 ? 'Completed' : 'Ongoing';
    },
    getStatusColor(item) {
      return Math.round(this.getCompletionPercentage(item)) === 100 ? 'success' : 'warning';
    },
    getCompletionPercentage(item) {
      // Use backend completion_percentage based on completed responses / associated experts
      return item && typeof item.completion_percentage === 'number' ? item.completion_percentage : 0;
    },
    getCompletedCount(item) {
      // We don't have completed count directly; estimate from percentage and total experts when available
      const total = this.getTotalExperts(item);
      const pct = this.getCompletionPercentage(item);
      if (total > 0 && typeof pct === 'number') {
        return Math.round((pct / 100) * total);
      }
      return 0;
    },
    getTotalExperts(item) {
      // Prefer expert_count from list serializer; else attempt from invitation_stats.total_experts
      if (typeof item.expert_count === 'number') return item.expert_count;
      if (item && item.invitation_stats && typeof item.invitation_stats.total_experts === 'number') {
        return item.invitation_stats.total_experts;
      }
      return 0;
    },
    goToDetail(item) {
      this.$router.push({ name: 'responseDetail', params: { id: item.id } });
    },
    onPageChange(newPage) {
      this.page = newPage;
    },
  },
  watch: {
    activeTab() {
      // pagination removed
    },
    acceptingResponses() {
      this.fetchAssessments();
    },
    page() {
      // pagination removed
    }
  },
  mounted() {
    this.fetchAssessments();
  }
};
</script>

<style scoped>
.responses-view {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
}

/* Tabs styling */
.response-tabs {
  max-width: 600px;
}

.active-tab {
  color: #e94057 !important;
}

/* Table card styling */
.response-table-card {
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);
}

.response-table :deep(table) {
  background-color: #f8f9fa;
  border-collapse: separate;
  border-spacing: 0 8px;
}

.response-table :deep(tr) {
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.response-table :deep(tr:hover) {
  background-color: #f8f9fa;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.response-table :deep(td) {
  padding: 12px 16px;
  border-bottom: none;
}

.response-table :deep(th) {
  font-weight: 600 !important;
  color: rgba(0, 0, 0, 0.6) !important;
  padding: 16px;
  border-bottom: none;
  text-transform: none;
}

.status-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.status-label {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.7);
}

/* Add horizontal padding for consistent side spaces */
.response-table {
  width: 100%;
  padding: 0 16px;
}

/* Custom Progress Bar Styles */
.custom-progress-container {
  min-width: 80px;
  width: 80px;
}

.custom-progress-track {
  width: 100%;
  height: 8px;
  background-color: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.custom-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #e94057 0%, #f16575 100%);
  border-radius: 4px;
  transition: width 0.3s ease-in-out;
  min-width: 0;
  animation: progressFill 0.3s ease-out;
}

@keyframes progressFill {
  from {
    transform: scaleX(0);
    transform-origin: left;
  }
  to {
    transform: scaleX(1);
    transform-origin: left;
  }
}

</style> 